import React from 'react';
import Layout from '../components/Layout';
import { Calendar, Video, MessageCircle, Check, Users, Star, Zap } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

function LessonsPage() {
  var { t, formatPrice } = useTranslation();

  var skills = [
    t('lessonsBreathing'),
    t('lessonsEmbouchure'),
    t('lessonsRange'),
    t('lessonsJazz'),
    t('lessonsClassical'),
    t('lessonsAudition'),
    t('lessonsAnxiety'),
    t('lessonsPractice')
  ];

  var groupPlanFeatures = [
    '4 One-hour Group classes per month',
    'Access to Private Music Collection',
    'Access to my designs',
    'Access to weekly videos',
    'Challenges / Retos',
    'Questions and Answers',
    'FREE Buzzinga',
    'Music in PDF',
    '30% discount on my products',
    'Weekly lessons',
    'Telegram group for questions',
    'Breathing Technique',
    'Improve Your Endurance',
    'Study Concertos',
    'Learn to Improvise',
    'Only 5 Students per month'
  ];

  var freePlanFeatures = [
    'Access to videos',
    'Challenges / Retos'
  ];

  return (
    <Layout>
      <SEO title={PAGE_SEO['lessons'].title} description={PAGE_SEO['lessons'].description} url="/lessons" />
      <div className="pt-32 pb-20 bg-black min-h-screen">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] text-sm tracking-widest uppercase mb-4">{t('lessonsLearnPro')}</p>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">{t('lessonsTitle')}</h1>
            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
              {t('lessonsSubtitle')}
            </p>
          </div>

          {/* Individual Lesson */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8">
                <Video className="w-10 h-10 text-[#d4af37] mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">{t('lessonsOnline')}</h3>
                <p className="text-neutral-400 mb-4">
                  {t('lessonsOnlineDesc')}
                </p>
                <p className="text-2xl font-bold text-[#d4af37] mb-5">{formatPrice(75)} {t('lessonsPerHour')}</p>
                <a
                  href="https://www.toneregistry.com/book-online"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-online-lesson"
                  className="block w-full text-center py-3.5 rounded-full bg-[#d4af37] text-black font-semibold hover:bg-white transition-all"
                >
                  <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> {t('lessonsBook')}</span>
                </a>
              </div>

              <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8">
                <MessageCircle className="w-10 h-10 text-[#d4af37] mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">{t('lessonsInPerson')}</h3>
                <p className="text-neutral-400 mb-4">
                  {t('lessonsInPersonDesc')}
                </p>
                <p className="text-2xl font-bold text-[#d4af37] mb-5">{formatPrice(100)} {t('lessonsPerHour')}</p>
                <a
                  href="https://www.toneregistry.com/book-online"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-inperson-lesson"
                  className="block w-full text-center py-3.5 rounded-full bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20 transition-all"
                >
                  <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> {t('lessonsBook')}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Membership Plans */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Online Programs</h2>
              <p className="text-neutral-400">Join our community and take your playing to the next level</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Group Plan - $40/month */}
              <div data-testid="plan-group" className="relative bg-neutral-900 border-2 border-[#d4af37]/50 rounded-2xl p-8 overflow-hidden">
                <div className="absolute top-4 right-4 px-3 py-1 bg-[#d4af37] text-black text-xs font-bold rounded-full">POPULAR</div>
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-[#d4af37]" />
                  <h3 className="text-xl font-bold text-white">MasterClass</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-bold text-white">{formatPrice(40)}</span>
                  <span className="text-neutral-500">/month</span>
                </div>
                <p className="text-neutral-400 text-sm mb-6">
                  Unlock your creative potential with JiMendez.com's MasterClass. Gain exclusive access to expert tutorials and resources.
                </p>
                <a
                  href="https://www.toneregistry.com/pricing-plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="select-group-plan"
                  className="block w-full text-center py-3.5 rounded-full bg-[#d4af37] text-black font-semibold hover:bg-white transition-all mb-6"
                >
                  Select
                </a>
                <ul className="space-y-3">
                  {groupPlanFeatures.map(function(feature, i) {
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
                        <span className="text-neutral-300 text-sm">{feature}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Free Plan */}
              <div data-testid="plan-free" className="bg-neutral-900 border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="w-8 h-8 text-neutral-400" />
                  <h3 className="text-xl font-bold text-white">Free Plan</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-bold text-white">{formatPrice(0)}</span>
                  <span className="text-neutral-500">/month</span>
                </div>
                <p className="text-neutral-400 text-sm mb-6">
                  Access to our free options. Create an account to get started.
                </p>
                <a
                  href="https://www.toneregistry.com/pricing-plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="select-free-plan"
                  className="block w-full text-center py-3.5 rounded-full bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20 transition-all mb-6"
                >
                  Select
                </a>
                <ul className="space-y-3">
                  {freePlanFeatures.map(function(feature, i) {
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" />
                        <span className="text-neutral-300 text-sm">{feature}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Skills Section */}
          <div className="bg-gradient-to-r from-[#d4af37]/10 to-transparent border border-[#d4af37]/20 rounded-2xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6">{t('lessonsWhatLearn')}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {skills.map(function(item, index) {
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#d4af37]" />
                    <span className="text-neutral-300">{item}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-6">{t('lessonsReady')}</h2>
            <a
              href="mailto:trumpet95@gmail.com"
              className="inline-flex items-center justify-center gap-3 bg-white/5 text-white border border-white/10 px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              {t('lessonsContactMe')}
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default LessonsPage;
